import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { Order, BatchedChanges, OrderStatus } from '@/types';
import { CardSection, ExpandableCard, ExpandableCardHandle, CardRow, CardTags, CardDropdownMenu } from '@/components/Cards';
import { faEdit, faTimes, faUserPlus, faFileTimes, faFileCheck, faBoxOpen } from '@fortawesome/pro-light-svg-icons';
import OrderExpand from './OrderExpand';
import theme from './OrderListItem.module.scss';
import { ProfileIcon } from '@/components/Icons';
import OrderChangeResponsibleDialog from './OrderChangeResponsibleDialog/OrderChangeResponsibleDialog';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Button } from '@/components/Buttons';
import ScrollToWrapper from '@/components/ScrollToWrapper';
import OrderStatusIndicator from '@/components/StatusIndicator/OrderStatusIndicator';
import OrderCancelDialog from './OrderCancelDialog';
import { RejectedStatusReason } from '@/components/StatusReason';
import { ResidentialUnitTooltip } from '../data-tooltips';
import { classnames } from '@/utils/html';
import { reopenOrderOptions, setOrderExcludeFromRoomFormFlag } from '@/services/orders';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateDisplay } from '@/components/localization';
import { Dropdown } from '@ygtechnologies/rubus-ui';
import { ProjectRelativeLink } from '@/components/navigation';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';

export interface OrderListItemProps {
    order: Order;
    onChange: (changes: BatchedChanges<Order>) => void;
    defaultExpanded?: boolean;
    readOnly?: boolean;
}

const OrderListItem: React.FC<OrderListItemProps> = ({ order, onChange, defaultExpanded, readOnly = false }) => {
    const [isExpanded, setIsExpanded] = useState(!!defaultExpanded);
    const [isLoaded, setIsLoaded] = useState(false);
    const [showResponsibleDialog, setShowResponsibleDialog] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);

    const handleResponsibleClick = () => {
        setShowResponsibleDialog(true);
    };

    const renderResponsibleButton = () => {
        const name = order.responsibleProfileName ? order.responsibleProfileName : <Trans>No responsible</Trans>;

        if (readOnly || !order.permissions.canChangeResponsible) {
            return <span className={theme.changeResponsibleButton}>{name}</span>;
        }

        return (
            <Button className={theme.changeResponsibleButton} onClick={handleResponsibleClick} icon={faEdit} color="text">
                {name}
            </Button>
        );
    };

    const handleCancelClick = async (canceledOrderIds: number[]) => {
        triggerHandler(onChange, BatchedChanges.repeat<Order>(canceledOrderIds, { status: OrderStatus.Canceled }));
    };

    const handleExcludeRoomFormChange = async (flag: boolean) => {
        await setOrderExcludeFromRoomFormFlag(order.id, flag);

        const updatedOrderLines = order.orderLines.map((x) => {
            return {
                ...x,
                excludeFromRoomForm: flag,
            };
        });

        triggerHandler(onChange, BatchedChanges.single<Order>(order.id, { orderLines: updatedOrderLines }));
    };

    const handleReopenOptions = async () => {
        const id = order.id;

        if (!id) return;

        try {
            await reopenOrderOptions(id);
            Toast.success(<Trans>Options have been reopened!</Trans>);
        } catch (e) {
            console.error(e);
            Toast.error(<Trans>Failed to reopen options</Trans>);
        }
    };

    const isExcludedFromRoomForm = order.orderLines.every((x) => x.excludeFromRoomForm);
    const renderContextMenu = () => {
        if (readOnly) {
            return null;
        }

        const isChangeRequest = !!order.changeRequestId;
        const isCancelable = order.status === OrderStatus.ReadyToStart && order.permissions.canApprove;
        const canExcludeFromRoomForm = order.status === OrderStatus.ReadyToStart && order.permissions.canExcludeFromRoomForm;
        const isReopenable = order.status === OrderStatus.Canceled && order.permissions.canApprove;

        return (
            <>
                <CardDropdownMenu hidden={!isCancelable && !isChangeRequest && !isReopenable}>
                    {isCancelable && (
                        <Dropdown.Item
                            icon={faTimes}
                            onClick={(e) => {
                                setShowCancelDialog(true);
                            }}>
                            <Trans>Cancel order</Trans>
                        </Dropdown.Item>
                    )}

                    {canExcludeFromRoomForm && isExcludedFromRoomForm && (
                        <Dropdown.Item
                            icon={faFileCheck}
                            onClick={(e) => {
                                handleExcludeRoomFormChange(false);
                            }}>
                            <Trans>Include in room form</Trans>
                        </Dropdown.Item>
                    )}

                    {canExcludeFromRoomForm && !isExcludedFromRoomForm && (
                        <Dropdown.Item
                            icon={faFileTimes}
                            onClick={(e) => {
                                handleExcludeRoomFormChange(true);
                            }}>
                            <Trans>Exclude from room form</Trans>
                        </Dropdown.Item>
                    )}

                    {isChangeRequest && (
                        <Dropdown.Item as={ProjectRelativeLink} to={`/changes/${order.changeRequestId}`}>
                            <Trans>Go to change request</Trans>
                        </Dropdown.Item>
                    )}

                    <Dropdown.Item disabled={!isReopenable} icon={faBoxOpen} onClick={() => handleReopenOptions()}>
                        <Trans>Reopen options</Trans>
                    </Dropdown.Item>
                </CardDropdownMenu>

                <OrderCancelDialog
                    order={order}
                    show={showCancelDialog}
                    onOrderCancel={handleCancelClick}
                    onClose={() => setShowCancelDialog(false)}
                />
            </>
        );
    };

    const excludedFromRoomFormElement = isExcludedFromRoomForm ? (
        <div className={theme.modifiers}>
            <small>
                <FontAwesomeIcon icon={faFileTimes} />
                <Trans>Excluded from room form</Trans>
            </small>
        </div>
    ) : null;

    function renderNameSection() {
        if (order.buyerOptionName) {
            return (
                <CardSection caption={order.buyerOptionGroupName} grow>
                    {order.buyerOptionName}
                    {excludedFromRoomFormElement}
                </CardSection>
            );
        }

        if (order.changeRequestName) {
            return (
                <CardSection caption={<Trans>Change request</Trans>} grow>
                    {order.changeRequestName}
                    {excludedFromRoomFormElement}
                </CardSection>
            );
        }

        if (order.buyerOptionPresetName) {
            return (
                <CardSection caption={<Trans>Style</Trans>} grow>
                    {order.buyerOptionPresetName}
                </CardSection>
            );
        }

        if (order.orderLines.length > 0) {
            return (
                <CardSection caption={<Trans>Description</Trans>} grow>
                    {order.orderLines[0].itemName}
                </CardSection>
            );
        }

        return null;
    }

    return (
        <ScrollToWrapper shouldScroll={defaultExpanded && isLoaded}>
            <ExpandableCard
                wrap
                expanded={isExpanded}
                type="inside-bordered"
                className={classnames(theme.card, { [theme.expanded]: isExpanded })}
                expandableContent={<OrderExpand order={order} onLoad={() => setIsLoaded(true)} onChange={onChange} />}>
                <CardRow>
                    <CardSection width="60px" caption={<Trans>Order ID</Trans>}>
                        #{order.orderNumber}
                    </CardSection>

                    <CardSection width="120px" caption={<Trans>Unit</Trans>}>
                        <ResidentialUnitTooltip residentialUnitId={order.residentialUnitId} linkTab="orders">
                            <span>{order.residentialUnitName}</span>
                        </ResidentialUnitTooltip>
                    </CardSection>

                    {renderNameSection()}

                    <CardSection right width="120px" caption={<Trans>Date</Trans>}>
                        <DateDisplay value={order.createDate} />
                    </CardSection>

                    <CardSection contentClassName={theme.responsible} right width="250px">
                        <ProfileIcon
                            profileId={order.responsibleProfileId || 0}
                            sizeInPx={40}
                            className={theme.responsibleAvatar}
                            emptyIcon={faUserPlus}
                        />
                        <CardSection caption={<Trans>Responsible</Trans>}>{renderResponsibleButton()}</CardSection>
                    </CardSection>

                    <CardSection caption={<Trans>Internal status</Trans>} width="130px" right>
                        <OrderStatusIndicator value={order.status} size="small" />

                        {order.status === OrderStatus.Rejected && (
                            <RejectedStatusReason
                                reason={order.rejectReason || ''}
                                userName={''} //TODO: add username
                            />
                        )}
                    </CardSection>

                    <OrderChangeResponsibleDialog
                        orderId={order.id}
                        projectId={order.projectId}
                        show={showResponsibleDialog}
                        onClose={() => setShowResponsibleDialog(false)}
                        onAccept={(orderIds, member) => {
                            triggerHandler(
                                onChange,
                                BatchedChanges.repeat<Order>(orderIds, {
                                    responsibleProfileId: member ? member.profileId : null,
                                    responsibleProfileName: member ? member.profileName : '',
                                })
                            );
                        }}
                    />

                    <ExpandableCardHandle expanded={isExpanded} onExpandToggle={() => setIsExpanded(!isExpanded)} />

                    {renderContextMenu()}
                </CardRow>

                <CardTags size="small" tags={order.tags} />
            </ExpandableCard>
        </ScrollToWrapper>
    );
};

export default OrderListItem;
